<template>
  <!-- Add Application Drawer  -->
  <ApplicationAddDrawer
    v-model:add-application-drawer="addApplicationDrawer"
    @create-custom-application="
      (application: any) => openCustomDialog(application)
    " />

  <!-- Page Content -->
  <main class="flex flex-col gap-6">
    <div class="no-print flex">
      <div class="table-controls-container w-full flex-wrap gap-3">
        <!-- Search -->
        <div class="min-w-[150px] max-w-[300px] flex-1">
          <SmInput
            v-model="searchTerm"
            outline
            size="medium"
            :label="i18n.t('views.applications.table.searchPlaceholder')">
          </SmInput>
        </div>

        <!-- Filters -->
        <div style="display: flex; align-items: center; gap: 10px">
          <SmDropdown
            :close-on-click-inside="false"
            dropdown-class="w-[32rem] max-w-7xl">
            <template #trigger>
              <SmButton outline size="small">
                <div class="flex items-center gap-2">
                  <Filter :size="18" />
                  <span>{{ i18n.t('filter') }}</span>
                  <span v-if="filterCount"> ({{ filterCount }}) </span>
                </div>
              </SmButton>
            </template>

            <div style="padding: 1rem">
              <ApplicationFilterDialog
                v-model:filter="filter"
                :close-on-click-inside="false" />
            </div>
          </SmDropdown>
        </div>

        <!-- Add Application -->
        <SmButton
          v-require-permission="CompanyAccessRole.ADMIN"
          class="ml-auto"
          @click="addApplicationDrawer = true">
          {{ i18n.t('views.applications.addSoftwareButtonText') }}
        </SmButton>
      </div>
    </div>
    <el-card
      shadow="always"
      class="relative grow overflow-y-auto"
      :body-style="{
        height: '100%',
        width: '100%',
      }">
      <ApplicationsTable
        ref="applicationTableRef"
        :filter="filter"
        @open-add-application-drawer="addApplicationDrawer = true" />
    </el-card>
  </main>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useDebounceFn } from '@vueuse/core'

  import { CompanyAccessRole } from '@/client'
  import ApplicationsTable from '@/components/applications/ApplicationsTable.vue'
  import { Application } from '@/types/software'
  import {
    ApplicationFilter,
    ApplicationSourceFilter,
  } from '@/stores/application/application.filter'
  import { useRoute, useRouter } from 'vue-router'
  import { Filter } from 'lucide-vue-next'
  import filterUtils from '@/utils/filter'

  const i18n = useI18n()
  const route = useRoute()
  const router = useRouter()

  const applicationTableRef = ref<ComponentExposed<typeof ApplicationsTable>>()

  function addFiltersToUrl() {
    const query = ref({ ...filter.value })

    // Workaround because 0 datasaource = all datasources
    if (
      query.value.isSource &&
      query.value.isSource.length ===
        Object.values(ApplicationSourceFilter).length
    ) {
      delete query.value.isSource
    }

    const normalizedQuery = filterUtils.urlEncodeFilters<ApplicationFilter>(
      query.value
    )

    router.push({ query: normalizedQuery })
  }

  const getInitialTagsFilter = (tagsQuery: unknown) => {
    if (!tagsQuery) {
      return []
    }

    const rawTags = (
      Array.isArray(tagsQuery) ? tagsQuery : [tagsQuery as string]
    ).flatMap((tag) => tag.split(','))

    return rawTags.map((tag) => tag.trim()).filter((tag) => tag !== '')
  }

  const getInitialSourceFilter = (sourceQuery: unknown) => {
    if (!sourceQuery) {
      return Object.values(ApplicationSourceFilter)
    }

    const rawSources = (
      Array.isArray(sourceQuery) ? sourceQuery : [sourceQuery as string]
    ).flatMap((source) => source.split(','))

    const normalizedSources = rawSources
      .map((source) => source.trim())
      .filter((source) => source !== '')

    return Object.values(ApplicationSourceFilter).filter((filterOption) =>
      normalizedSources.includes(filterOption)
    )
  }

  // ----------------------------------------------------------------------
  // Set Filter
  // ----------------------------------------------------------------------
  const filter = ref<ApplicationFilter>({
    tags: undefined,
    isSSOProvider: false,
    isSource: [
      ApplicationSourceFilter.SSO,
      ApplicationSourceFilter.INTEGRATION,
      ApplicationSourceFilter.MANUALLY,
    ],
    search: undefined,
    tags: getInitialTagsFilter(route.query['tags']),
    isSSOProvider: route.query['isSSOProvider'] === 'true',
    isSource: getInitialSourceFilter(route.query['isSource']),
    search: route.query['search'] ? (route.query['search'] as string).trim() : undefined,
    hideWithoutCosts: route.query['hideWithoutCosts'] === 'true',
    hideWithCosts: route.query['hideWithCosts'] === 'true',
    hideWithoutAccount: route.query['hideWithoutAccount'] === 'true',
  })

  // Handle filter count
  const filterCount = computed(() => {
    return Object.keys(filter.value).filter((key) => {
      const filterKey = filter.value[key as keyof ApplicationFilter]

      // check if isSource is empty
      if (Array.isArray(filterKey)) {
        return filterKey.length > 0
      }

      return (
        filterKey !== undefined && filterKey !== null && filterKey !== false
      )
    }).length
  })

  const searchTerm = ref(filter.value.search ?? '')

  const applySearchFilter = useDebounceFn((raw: string) => {
    const trimmedQuery = raw.trim()
    filter.value.search = trimmedQuery ? trimmedQuery : undefined
  }, 200)

  watch(searchTerm, (value) => {
    applySearchFilter(value)
  })

  watch(filter.value, () => {
    addFiltersToUrl()
  })

  // ----------------------------------------------------------------------
  // Drawer
  // ----------------------------------------------------------------------
  const addApplicationDrawer = ref(false)
  const customApplicationTemplate = ref<Application>()
  const createCustomApplication = ref(false)

  const openCustomDialog = (application: Application) => {
    customApplicationTemplate.value = application
    createCustomApplication.value = true
  }

  watchEffect(() => {
    if (route.query['open-add-application-drawer']) {
      addApplicationDrawer.value =
        route.query['open-add-application-drawer'] === 'true'
    }
  })
</script>
